import firebaseApp from "../firebase/credenciales";
import {
  getFirestore,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from "firebase/firestore";

/**
 * Capa de datos unificada para el reporte de envío / recepción de documentos.
 *
 * La aplicación distribuye los documentos en varias ubicaciones:
 *   - `document`        → boletas, CTS, memorándums, vacaciones, ceses, etc.
 *   - `uploadDocument`  → recibos de honorarios y (para el RUC 20609298589) todo.
 *   - `legajo`          → `onboarding/{dni}/legajo`: contratos, adendas y examen médico.
 *
 * Este módulo consulta las tres fuentes, normaliza los registros a un esquema
 * común y aplica los filtros de año / periodo / tipo de documento.
 *
 * Optimizaciones aplicadas:
 *   - Los filtros de RUC y año se aplican EN LA CONSULTA (no en el cliente).
 *   - Una sola consulta de `users` para enriquecer los nombres (cacheada).
 *   - Se eliminan las lecturas N+1: nunca se consulta un documento por DNI.
 */

const db = getFirestore(firebaseApp);

const COLECCION_LEGAJO = "legajo";
const TAMANO_LOTE_DNI = 30; // límite de Firebase para el operador "in"

/**
 * Estrategia de consulta por colección.
 *
 * El esquema de `ano` NO es uniforme en la base de datos:
 *   - `document`        → `ano` como texto ("2026") y `periodo` ("01".."12").
 *   - `uploadDocument`  → `ano` como número (reciboHonorarios) y `mes` en vez de `periodo`.
 *   - `legajo`          → `ano` como número y `mes` en vez de `periodo`.
 *
 * Firestore compara por tipo, así que filtrar un año textual sobre una
 * colección que lo guarda numérico no devuelve NADA. Por eso solo se filtra
 * el año en la consulta cuando el tipo de dato es conocido con certeza; en el
 * resto de casos el filtro se aplica en el cliente con comparación tolerante.
 */
const ESTRATEGIA = {
  document: { anoEnConsulta: "texto", periodoEnConsulta: true },
  uploadDocument: { anoEnConsulta: null, periodoEnConsulta: false },
  legajo: { anoEnConsulta: "numero", periodoEnConsulta: false },
};

const CACHE_TTL_MS = 5 * 60 * 1000;
const cacheUsuarios = new Map();
const inflightUsuarios = new Map();

// ───────────────────────────────────────────────────────────────────────────
// Helpers puros (exportados para pruebas unitarias)
// ───────────────────────────────────────────────────────────────────────────

const normalizar = (valor) => String(valor || "").trim().toLowerCase();

/** Texto limpio: nunca "undefined" ni "null". */
export function textoNoVacio(valor) {
  if (valor === null || valor === undefined) return "";
  const texto = String(valor).trim();
  if (texto === "undefined" || texto === "null") return "";
  return texto;
}

/**
 * Devuelve la lista de tipos concretos que equivalen al tipo solicitado.
 * Se usa para el filtro local y como base del filtro en la consulta.
 */
export function aliasDeTipo(tipoObjetivo) {
  const objetivo = normalizar(tipoObjetivo);
  if (!objetivo || objetivo === "todos") return [];

  switch (objetivo) {
    case "boletas de pago":
      return ["boletas de pago", "boleta de pago", "boleta", "boletas", "boleta pago"];
    case "cts":
      return ["cts"];
    case "contratos":
      return ["contratos", "contrato", "contrato de trabajo", "adenda", "adenda contrato"];
    case "renta":
      return ["renta", "renta de 5ta", "renta 5ta"];
    case "memorandums":
      return ["memorandums", "memorandum", "memorandos", "memo"];
    case "utilidades":
      return ["utilidades", "utilidad"];
    case "gratificaciones":
      return ["gratificaciones", "gratificacion", "gratificación"];
    case "vacaciones":
      return ["vacaciones", "vacacion"];
    case "prestamos":
      return ["prestamos", "préstamos", "prestamo", "préstamo"];
    case "adelantos":
      return ["adelantos", "adelanto"];
    case "politicaslaborales":
      return ["politicas laborales", "politica laboral", "politica salarial", "política salarial"];
    case "ceses":
      return ["ceses", "cese", "liquidaciones", "liquidacion"];
    case "otros":
      return ["otros"];
    default:
      return [objetivo];
  }
}

/** Coincidencia tolerante entre el tipo registrado y el tipo solicitado. */
export function coincideTipo(tipoRegistro, tipoObjetivo) {
  const objetivo = normalizar(tipoObjetivo);
  if (!objetivo || objetivo === "todos") return true;

  const registrado = normalizar(tipoRegistro);
  if (!registrado) return false;

  if (aliasDeTipo(objetivo).includes(registrado)) return true;

  // Coincidencia por subcadena para los tipos con variantes abiertas.
  if (objetivo === "renta") return registrado.includes("renta");
  if (objetivo === "utilidades") return registrado.includes("utilidad");
  if (objetivo === "gratificaciones") return registrado.includes("grati");
  if (objetivo === "vacaciones") return registrado.includes("vacacion");
  if (objetivo === "prestamos") return registrado.includes("prestam");
  if (objetivo === "adelantos") return registrado.includes("adelant");
  if (objetivo === "politicaslaborales") {
    return (
      registrado.includes("politica") ||
      registrado.includes("salarial") ||
      registrado.includes("reglamento")
    );
  }
  if (objetivo === "ceses") return registrado.includes("cese") || registrado.includes("liquida");
  if (objetivo === "contratos") return registrado.includes("contrato") || registrado.includes("adenda");
  if (objetivo === "memorandums") {
    return registrado.includes("memorandum") || registrado.includes("memo");
  }
  if (objetivo === "boletas de pago") return registrado.includes("boleta");

  return registrado === objetivo;
}

/** Periodo ("01".."12") de un registro, sea `periodo` o `mes`. */
export function extraerPeriodo(registro) {
  const candidatos = [registro?.periodo, registro?.mes];
  const crudo = candidatos.find(
    (valor) => valor !== undefined && valor !== null && String(valor).trim() !== ""
  );
  const texto = String(crudo ?? "").trim();
  if (!texto) return "";
  if (/^\d{1,2}$/.test(texto)) return texto.padStart(2, "0");
  return texto;
}

/** Año de un registro. */
export function extraerAnio(registro) {
  return String(registro?.ano ?? registro?.year ?? "").trim();
}

/** Normaliza una lista de DNIs, incluyendo la variante con ceros a la izquierda. */
export function normalizarDnis(values = []) {
  const set = new Set();
  values.forEach((value) => {
    const limpio = String(value || "").trim();
    if (!limpio) return;
    set.add(limpio);
    set.add(limpio.padStart(8, "0"));
  });
  return Array.from(set);
}

function lotes(arr, tamano) {
  const salida = [];
  for (let i = 0; i < arr.length; i += tamano) {
    salida.push(arr.slice(i, i + tamano));
  }
  return salida;
}

// ───────────────────────────────────────────────────────────────────────────
// Acceso a Firestore
// ───────────────────────────────────────────────────────────────────────────

const esErrorDeIndice = (error) => {
  const code = String(error?.code || "").toLowerCase();
  return code.includes("failed-precondition") || code.includes("unimplemented");
};

/**
 * Ejecuta una consulta sobre `ref` y, si falla por índice no desplegado,
 * degrada progresivamente a las alternativas provistas.
 */
async function consultarConFallback(ref, constraints, fallbacks = []) {
  const intentos = [constraints, ...fallbacks];

  let ultimoError = null;
  for (const intento of intentos) {
    try {
      const q = intento.length ? query(ref, ...intento) : query(ref);
      return await getDocs(q);
    } catch (error) {
      ultimoError = error;
      if (!esErrorDeIndice(error)) throw error;
    }
  }

  throw ultimoError;
}

/** Datos de la colección `users` (cacheados para evitar lecturas repetidas). */
async function obtenerUsuarios(ruc) {
  const clave = String(ruc || "").trim();
  if (!clave) return new Map();

  const cached = cacheUsuarios.get(clave);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  if (inflightUsuarios.has(clave)) {
    return inflightUsuarios.get(clave);
  }

  const promesa = (async () => {
    const mapa = new Map();
    try {
      const ref = collection(db, "users");
      const snapshot = await getDocs(query(ref, where("rucEmpresa", "==", clave)));
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const dni = String(data.dni || "").trim();
        if (!dni) return;
        mapa.set(dni, {
          nombre: data.name || "",
          apellidos: data.apellidos || "",
          email: data.email || "",
          nombreCompleto: `${data.apellidos || ""} ${data.name || ""}`.trim(),
        });
      });
    } catch (error) {
      console.warn("No se pudieron cargar los usuarios de la empresa:", error);
    }

    cacheUsuarios.set(clave, { timestamp: Date.now(), data: mapa });
    return mapa;
  })();

  inflightUsuarios.set(clave, promesa);
  try {
    return await promesa;
  } finally {
    inflightUsuarios.delete(clave);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// API principal
// ───────────────────────────────────────────────────────────────────────────

/**
 * @param {Object} opciones
 * @param {string} opciones.ruc                RUC de la empresa.
 * @param {string} [opciones.ano]              Año del reporte.
 * @param {string} [opciones.periodoInicio]    Periodo inicial ("01".."12").
 * @param {string} [opciones.periodoFin]       Periodo final ("01".."12").
 * @param {string} [opciones.tipoDocumento]    Tipo de documento o "Todos".
 * @param {string[]} [opciones.dniList]        DNIs a incluir (selección del usuario).
 * @param {boolean} [opciones.incluirTodos]    Si es true ignora `dniList`.
 * @returns {Promise<Array>} Registros normalizados listos para el PDF.
 */
export default async function getReporteDocumentos(opciones = {}) {
  const {
    ruc,
    ano,
    periodoInicio,
    periodoFin,
    tipoDocumento = "Todos",
    dniList = [],
    incluirTodos = false,
  } = opciones;

  const rucLimpio = String(ruc || "").trim();
  const anoLimpio = String(ano || "").trim();
  const tipoObjetivo = String(tipoDocumento || "Todos").trim() || "Todos";

  const pInicio = String(periodoInicio || "").trim().padStart(2, "0");
  const pFin = String(periodoFin || "").trim().padStart(2, "0");
  const filtrarPeriodo = Boolean(pInicio && pFin && pInicio !== "00" && pFin !== "00");

  const dnis = incluirTodos ? [] : normalizarDnis(dniList);
  const setDnis = new Set(dnis);

  const plan = planDeLectura(tipoObjetivo);
  const usuarios = await obtenerUsuarios(rucLimpio);

  const contexto = {
    rucLimpio,
    anoLimpio,
    tipoObjetivo,
    pInicio,
    pFin,
    filtrarPeriodo,
    setDnis,
    dnis,
    usuarios,
  };

  // ── Lectura de las colecciones planificadas para el tipo seleccionado ────
  const registros = [];
  for (const nombre of plan.colecciones) {
    const leidos = await leerColeccion(nombre, contexto);
    registros.push(...leidos);
  }

  let resultado = aplicarFiltros(registros, contexto);

  // ── Respaldo: si el tipo es laboral y `document` no arrojó resultados, se
  //    consulta `uploadDocument` (donde el RUC 20609298589 guarda TODOS sus
  //    documentos). Solo se paga esta consulta cuando no hay nada que mostrar.
  if (plan.conRespaldo && resultado.length === 0) {
    const leidosRespaldo = await leerColeccion("uploadDocument", contexto);
    if (leidosRespaldo.length > 0) {
      resultado = aplicarFiltros(leidosRespaldo, contexto);
    }
  }

  return resultado;
}

/**
 * Define qué colecciones se consultan según el tipo de documento elegido.
 *
 *  - "Todos"        → las tres (cobertura completa).
 *  - "Contratos"    → las tres: un contrato puede haberse subido manualmente
 *                     a `document` o haberse generado en `legajo`.
 *  - Cualquier otro → SOLO `document`, que es donde se registran los
 *                     documentos laborales. `uploadDocument` queda como
 *                     respaldo y solo se consulta si no hay resultados.
 */
export function planDeLectura(tipoDocumento) {
  const objetivo = normalizar(tipoDocumento);

  if (!objetivo || objetivo === "todos") {
    return {
      colecciones: ["document", "uploadDocument", COLECCION_LEGAJO],
      conRespaldo: false,
    };
  }

  if (objetivo === "contratos") {
    return {
      colecciones: [COLECCION_LEGAJO, "document", "uploadDocument"],
      conRespaldo: false,
    };
  }

  return { colecciones: ["document"], conRespaldo: true };
}

/** Lee una colección (o el grupo `legajo`) aplicando los filtros de consulta. */
async function leerColeccion(nombre, contexto) {
  const { rucLimpio, anoLimpio, pInicio, pFin, filtrarPeriodo, dnis } = contexto;
  const salida = [];

  const esLegajo = nombre === COLECCION_LEGAJO;
  const ref = esLegajo ? collectionGroup(db, COLECCION_LEGAJO) : collection(db, nombre);
  const estrategia = ESTRATEGIA[nombre] || ESTRATEGIA.document;

  const base = [];
  if (rucLimpio) base.push(where("ruc", "==", rucLimpio));

  if (dnis.length > 0) {
    // Consulta por DNI: índice (ruc, dni). Se leen todos los años del DNI
    // seleccionado y el año se filtra en el cliente.
    for (const lote of lotes(dnis, TAMANO_LOTE_DNI)) {
      const restricciones = esLegajo
        ? [where("dni", "in", lote)]
        : [...base, where("dni", "in", lote)];

      const snapshot = await consultarConFallback(
        ref,
        restricciones,
        esLegajo ? [] : [[where("dni", "in", lote)]]
      );
      snapshot.forEach((docSnap) => {
        salida.push({ ...docSnap.data(), __id: docSnap.id, __origen: nombre });
      });
    }
    return salida;
  }

  // Consulta por empresa. El año solo se aplica en la consulta cuando el tipo
  // de dato almacenado es conocido; en caso contrario se filtra en el cliente
  // con comparación tolerante a texto/número.
  const constraints = [...base];
  const fallbacks = [];

  if (estrategia.anoEnConsulta === "texto" && anoLimpio) {
    constraints.push(where("ano", "==", anoLimpio));
  } else if (estrategia.anoEnConsulta === "numero" && anoLimpio) {
    constraints.push(where("ano", "==", Number(anoLimpio)));
  }

  if (estrategia.periodoEnConsulta && filtrarPeriodo) {
    constraints.push(where("periodo", ">=", pInicio), where("periodo", "<=", pFin));
  }

  // Si la consulta compuesta falla por índice, se degrada a empresa.
  if (constraints.length > base.length) {
    fallbacks.push([...base]);
  }

  const snapshot = await consultarConFallback(ref, constraints, fallbacks);
  snapshot.forEach((docSnap) => {
    salida.push({ ...docSnap.data(), __id: docSnap.id, __origen: nombre });
  });

  return salida;
}

/** Aplica los filtros en el cliente y normaliza al esquema del reporte. */
function aplicarFiltros(registros, contexto) {
  const {
    rucLimpio,
    anoLimpio,
    tipoObjetivo,
    pInicio,
    pFin,
    filtrarPeriodo,
    setDnis,
    usuarios,
  } = contexto;

  const resultado = [];

  for (const registro of registros) {
    if (!registro) continue;

    // Empresa (el legajo puede consultarse por DNI: filtro de seguridad)
    if (rucLimpio) {
      const rucRegistro = String(registro.ruc || registro.rucEmpresa || "").trim();
      if (rucRegistro && rucRegistro !== rucLimpio) continue;
    }

    // DNI (cuando se consultó una lista concreta)
    if (setDnis.size > 0) {
      const dniRegistro = String(registro.dni || "").trim();
      if (!setDnis.has(dniRegistro) && !setDnis.has(dniRegistro.padStart(8, "0"))) continue;
    }

    // Documento inactivo
    if (registro.activo === false) continue;

    // Tipo de documento
    if (!coincideTipo(registro.tipo, tipoObjetivo)) continue;

    // Año
    if (anoLimpio) {
      const anioRegistro = extraerAnio(registro);
      if (anioRegistro && anioRegistro !== anoLimpio) continue;
    }

    // Periodo
    if (filtrarPeriodo) {
      const periodoRegistro = extraerPeriodo(registro);
      if (periodoRegistro && (periodoRegistro < pInicio || periodoRegistro > pFin)) continue;
    }

    // Colaborador
    const dniRegistro = String(registro.dni || "").trim();
    const usuario = usuarios.get(dniRegistro) || usuarios.get(dniRegistro.padStart(8, "0"));

    resultado.push({
      id: registro.__id,
      dni: dniRegistro,
      colaborador:
        textoNoVacio(registro.colaborador) ||
        (usuario ? usuario.nombreCompleto : "") ||
        textoNoVacio(registro.nombre),
      email: textoNoVacio(registro.email) || (usuario ? usuario.email : ""),
      nombres: usuario ? usuario.nombre : textoNoVacio(registro.nombres),
      apellidos: usuario ? usuario.apellidos : textoNoVacio(registro.apellidos),
      tipo: textoNoVacio(registro.tipo) || textoNoVacio(registro.nombre),
      periodo: extraerPeriodo(registro),
      ano: extraerAnio(registro),
      fechaDoc: registro.fechaDoc || null,
      fechaEnvio: registro.fechaEnvio || null,
      fechaVista: registro.fechaVista || null,
      fechaFirma: registro.fechaFirma || null,
      link: registro.link || null,
      linkFirmado: registro.linkFirmado || null,
      estadoEnLegajo: registro.estadoEnLegajo || null,
      contador: typeof registro.contador === "number" ? registro.contador : 0,
      plataforma: textoNoVacio(registro.plataforma),
      enviado: registro.enviado ?? null,
      nombre: textoNoVacio(registro.nombre),
      ruc: String(registro.ruc || rucLimpio || "").trim(),
      origen: registro.__origen || "",
    });
  }

  return resultado;
}

/** Limpia la caché de usuarios (usado en pruebas). */
export function limpiarCacheUsuarios() {
  cacheUsuarios.clear();
  inflightUsuarios.clear();
}
